import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../config';

export interface CreateApiKeyInput {
  name: string;
  expiresAt?: string;
  rateLimit?: number;
  ipWhitelist?: string[];
}

export interface UpdateApiKeyInput {
  name?: string;
  expiresAt?: string;
  rateLimit?: number;
  ipWhitelist?: string[];
  isActive?: boolean;
}

export interface CreateApiKeyResponse {
  id: string;
  key: string;
  keyPrefix: string;
  name: string;
  expiresAt?: string;
  createdAt: string;
  message: string;
}

export const useCreateApiKey = () => {
  const queryClient = useQueryClient();

  return useMutation<CreateApiKeyResponse, Error, CreateApiKeyInput>({
    mutationFn: async (data) => {
      return api.post<CreateApiKeyResponse>('/api/v1/api-keys', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apiKeys'] });
    },
  });
};

export const useUpdateApiKey = () => {
  const queryClient = useQueryClient();

  return useMutation<{ message: string }, Error, { keyId: string; data: UpdateApiKeyInput }>({
    mutationFn: async ({ keyId, data }) => {
      return api.patch<{ message: string }>(`/api/v1/api-keys/${keyId}`, data);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['apiKeys'] });
      queryClient.invalidateQueries({ queryKey: ['apiKey', variables.keyId] });
    },
  });
};

export const useRevokeApiKey = () => {
  const queryClient = useQueryClient();

  return useMutation<{ message: string }, Error, string>({
    mutationFn: async (keyId) => {
      return api.delete<{ message: string }>(`/api/v1/api-keys/${keyId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apiKeys'] });
    },
  });
};

export const useReactivateApiKey = () => {
  const queryClient = useQueryClient();

  return useMutation<{ message: string }, Error, string>({
    mutationFn: async (keyId) => {
      return api.post<{ message: string }>(`/api/v1/api-keys/${keyId}/reactivate`, {});
    },
    onSuccess: (_, keyId) => {
      queryClient.invalidateQueries({ queryKey: ['apiKeys'] });
      queryClient.invalidateQueries({ queryKey: ['apiKey', keyId] });
    },
  });
};

export interface RegenerateApiKeyResponse {
  key: string;
  keyPrefix: string;
  message: string;
}

export const useRegenerateApiKey = () => {
  const queryClient = useQueryClient();

  return useMutation<RegenerateApiKeyResponse, Error, string>({
    mutationFn: async (keyId) => {
      return api.post<RegenerateApiKeyResponse>(`/api/v1/api-keys/${keyId}/regenerate`, {});
    },
    onSuccess: (_, keyId) => {
      queryClient.invalidateQueries({ queryKey: ['apiKeys'] });
      queryClient.invalidateQueries({ queryKey: ['apiKey', keyId] });
      queryClient.invalidateQueries({ queryKey: ['apiKeyHistory', keyId] });
    },
  });
};

export const useSoftDeleteApiKey = () => {
  const queryClient = useQueryClient();

  return useMutation<{ message: string }, Error, { keyId: string; reason?: string }>({
    mutationFn: async ({ keyId, reason }) => {
      return api.post<{ message: string }>(`/api/v1/api-keys/${keyId}/delete`, { reason });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apiKeys'] });
      queryClient.invalidateQueries({ queryKey: ['apiKeys', 'trash'] });
    },
  });
};

export const useRestoreApiKey = () => {
  const queryClient = useQueryClient();

  return useMutation<{ message: string }, Error, string>({
    mutationFn: async (keyId) => {
      return api.post<{ message: string }>(`/api/v1/api-keys/${keyId}/restore`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apiKeys'] });
      queryClient.invalidateQueries({ queryKey: ['apiKeys', 'trash'] });
    },
  });
};

export const usePermanentlyDeleteApiKey = () => {
  const queryClient = useQueryClient();

  return useMutation<{ message: string }, Error, { keyId: string; reason?: string }>({
    mutationFn: async ({ keyId, reason }) => {
      return api.delete<{ message: string }>(`/api/v1/api-keys/${keyId}/permanent`, {
        confirmation: 'PERMANENTLY DELETE',
        reason,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apiKeys', 'trash'] });
    },
  });
};